import { env } from '@/shared/config';

import { ApiError } from './api-error';

const DEFAULT_TIMEOUT_MS = 15_000;

interface HttpAuth {
  /** Текущий токен для заголовка Authorization. */
  getToken: () => string | null;
  /**
   * Что делать на 401: вернуть новый токен — запрос повторится один раз,
   * вернуть null — ошибка уйдёт наверх. Реализация обязана быть
   * single-flight, иначе параллельные запросы устроят шквал входов.
   */
  refreshToken: () => Promise<string | null>;
  /**
   * Демо-ответчик: подменяет сеть на dev-площадке без бэкенда.
   *
   * `undefined` из него означает «такого пути в заглушке нет» — запрос
   * уйдёт в сеть как обычно, и разработчик увидит честную ошибку, а не
   * пустой экран. Ставится только слоем app и только при `env.demo`.
   */
  demo?: (path: string, options: RequestOptions) => unknown;
}

/**
 * Транспорт ничего не знает про стор сессии: связку делает слой app
 * (`app/providers/configure-api.ts`). Так `shared` не зависит от `entities`.
 */
let auth: HttpAuth = {
  getToken: () => null,
  refreshToken: () => Promise.resolve(null),
};

export function configureHttp(next: HttpAuth): void {
  auth = next;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  /** Публичный эндпоинт: не подставлять токен и не пытаться перелогиниться. */
  anonymous?: boolean;
  signal?: AbortSignal;
  timeoutMs?: number;
}

export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  if (auth.demo) {
    const canned = auth.demo(path, options);
    if (canned !== undefined) return canned as T;
  }

  const url = buildUrl(path, options.query);
  const response = await send(url, options, auth.getToken());

  if (response.status === 401 && !options.anonymous) {
    const token = await auth.refreshToken();
    if (token) {
      return parse<T>(await send(url, options, token));
    }
  }

  return parse<T>(response);
}

/**
 * Тайм-аут запроса без `AbortSignal.timeout`.
 *
 * ПОЧЕМУ НЕ ШТАТНЫЙ МЕТОД. `AbortSignal.timeout` появился в Safari 16,
 * `AbortSignal.any` — только в 17.4. Мини-апп живёт в WebView Telegram,
 * а он берёт движок системы: на iPhone 7 с iOS 15.8 обоих методов нет,
 * и вход падал на первом же запросе с «AbortSignal.timeout is not
 * a function» (жалоба игрока 08.09.2026). Сборка целится в es2022, но это
 * про СИНТАКСИС — рантайм-методы транспиляция не добавляет.
 *
 * `AbortController` и `setTimeout` есть везде, начиная с Safari 11.
 * Причину прерывания никто не читает, поэтому `abort()` зовётся без
 * аргумента: параметр `reason` — тоже Safari 16.
 *
 * `cleanup` обязателен: без него таймер держит колбэк до срабатывания,
 * а на экране матчей это два десятка запросов подряд.
 */
function requestSignal(
  timeoutMs: number,
  external: AbortSignal | undefined,
): { signal: AbortSignal; cleanup: () => void } {
  const controller = new AbortController();
  const timer = setTimeout(() => {
    controller.abort();
  }, timeoutMs);

  const onExternalAbort = (): void => {
    controller.abort();
  };

  if (external) {
    if (external.aborted) controller.abort();
    else external.addEventListener('abort', onExternalAbort, { once: true });
  }

  return {
    signal: controller.signal,
    cleanup: () => {
      clearTimeout(timer);
      external?.removeEventListener('abort', onExternalAbort);
    },
  };
}

async function send(
  url: string,
  options: RequestOptions,
  token: string | null,
): Promise<Response> {
  const headers = new Headers({ Accept: 'application/json' });
  if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  if (token && !options.anonymous) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const { signal, cleanup } = requestSignal(
    options.timeoutMs ?? DEFAULT_TIMEOUT_MS,
    options.signal,
  );

  try {
    return await fetch(url, {
      method: options.method ?? 'GET',
      headers,
      signal,
      ...(options.body === undefined
        ? {}
        : { body: JSON.stringify(options.body) }),
    });
  } catch (cause) {
    throw ApiError.network(cause);
  } finally {
    cleanup();
  }
}

async function parse<T>(response: Response): Promise<T> {
  const body = await readBody(response);

  if (!response.ok) {
    throw ApiError.fromResponse(response.status, body);
  }

  return body as T;
}

async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null;

  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const base = env.apiBaseUrl.replace(/\/$/, '');
  const url = `${base}${path.startsWith('/') ? path : `/${path}`}`;

  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }

  const search = params.toString();
  return search ? `${url}?${search}` : url;
}
